<script setup>
import Dialog from 'primevue/dialog'
import FileUpload from 'primevue/fileupload'
import axios from 'axios'
import { ref, computed, defineProps } from 'vue'
import Swal from 'sweetalert2'

const props = defineProps({
  monthlyActualReportId: { type: String, default: '' },
  monthlyActualReportName: { type: String, default: '' }
})

function showModal() {
  displayModal.value = true
}

function setPdf(e) {
  if (e.files.length === 0) return

  formData.value.pdf = e.files[0]
}

const pdfUrl = computed(() => {
  return URL.createObjectURL(formData.value.pdf)
})

const swalMixin = () => {
  return Swal.mixin({
    customClass: {
      confirmButton:
        'group flex items-center justify-center rounded-md border bg-green-400 py-2 pr-4 pl-3 text-sm font-medium text-white shadow-sm border-transparent hover:bg-green-500 sm:w-auto',
      cancelButton:
        'group mr-4 flex items-center justify-center rounded-md border border-transparent bg-gray-600 py-2 pl-3 pr-4 text-sm font-medium text-white shadow-sm sm:w-auto hover:bg-gray-800'
    },
    buttonsStyling: false
  })
}
async function submit() {
  const url = `/monthly_actual_reports/${props.monthlyActualReportId}/purchase_orders`
  const data = new FormData()
  data.append('purchase_order[purchase_order_pdf]', formData.value.pdf)
  axios.defaults.headers.common['X-CSRF-Token'] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute('content')
  const headers = { 'content-type': 'multipart/form-data' }
  isLoading.value = true
  try {
    const res = await axios.post(url, data, { headers })
    console.log(res)
    if (res.status === 200) {
      isLoading.value = false
      displayModal.value = false
      const modal = await swalMixin().fire({
        title: '',
        text: `${props.monthlyActualReportName} 様の注文書PDFを登録しました`,
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        showCancelButton: false
      })
      if (modal.isConfirmed) {
        location.reload()
      }
    }
  } catch (error) {
    console.error('リクエストでエラーが発生しました:', error)
    isLoading.value = false
  }
}

const isLoading = ref(false)
const displayModal = ref(false)
const formData = ref({
  pdf: null
})
</script>
<template>
  <button
    class="ml-1 inline-flex items-center rounded-md border border-gray-500 bg-white px-3 py-1 text-sm font-medium text-gray-800 shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
    @click="showModal"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="h-5 w-5"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
      />
    </svg>
    <span class="pl-1">アップロード</span>
  </button>
  <Dialog
    v-model:visible="displayModal"
    :header="`${props.monthlyActualReportName} 様 注文書アップロード`"
    :modal="true"
    :style="{ width: '60vw' }"
  >
    <FileUpload
      name="purchaseOrder"
      :multiple="false"
      accept="application/pdf"
      :max-file-size="10485760"
      :show-upload-button="false"
      :show-cancel-button="false"
      invalid-file-type-message="PDFファイルをアップロードしてください"
      invalid-file-size-message="ファイルサイズが{1}より大きいです"
      @select="setPdf"
    >
      <template #header="{ chooseCallback, clearCallback, files }">
        <button
          class="ml-1 inline-flex items-center rounded-md border border-gray-500 bg-white px-3 py-1 text-sm font-medium text-gray-800 shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
          @click="chooseCallback()"
        >
          ファイルを選択
        </button>
        <button
          class="ml-1 inline-flex items-center rounded-md border border-gray-500 bg-white px-3 py-1 text-sm font-medium text-gray-800 shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
          @click="clearCallback()"
        >
          クリア
        </button>
        <span v-if="files.length > 0" class="font-semibold">
          {{ files[0].name }}
        </span>
      </template>
      <template #content="{ files }">
        <div v-if="files.length > 0" class="h-[60vh]">
          <div class="flex h-full flex-col">
            <object
              :data="pdfUrl"
              type="application/pdf"
              class="w-full flex-1"
            />
            <div class="mt-8 flex flex-row-reverse">
              <button
                class="ml-6 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
                @click="submit"
              >
                アップロード
              </button>
            </div>
          </div>
        </div>
      </template>
      <template #empty>
        <div
          class="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
        >
          <div class="text-center">
            <svg
              class="mx-auto h-12 w-12 text-gray-300"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
                clip-rule="evenodd"
              />
            </svg>
            <div class="mt-4 flex text-sm leading-6 text-gray-600">
              <p class="pl-1">
                上記でファイルを選択するかドラッグ&ドロップしてください
              </p>
            </div>
            <p class="text-xs leading-5 text-gray-600">
              PDFを1ファイル 最大10MBまで
            </p>
          </div>
        </div>
      </template>
    </FileUpload>

    <vue-element-loading :active="isLoading" />
  </Dialog>
</template>
